
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Application {
  id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  message: string;
  status: 'new' | 'reviewing' | 'completed' | 'archived';
  type: 'contact' | 'demo' | 'newsletter';
  created_at: string;
  updated_at: string;
}

const AdminApplications = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadApplications();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applications, filters]);

  const loadApplications = async () => {
    try {
      setIsLoading(true);
      
      // Tüm tabloları birleştir
      const [contactRes, demoRes, newsletterRes] = await Promise.all([
        supabase.from('contact_submissions').select('*'),
        supabase.from('demo_requests').select('*'),
        supabase.from('newsletter_subscriptions').select('*')
      ]);

      const allApplications: Application[] = [
        ...(contactRes.data || []).map(item => ({
          id: item.id,
          name: item.name || 'İsimsiz',
          email: item.email,
          phone: item.phone || '',
          company: item.company || '',
          message: item.message || '',
          status: 'new' as const,
          type: 'contact' as const,
          created_at: item.created_at,
          updated_at: item.created_at
        })),
        ...(demoRes.data || []).map(item => ({
          id: item.id,
          name: item.name || 'İsimsiz',
          email: item.email,
          phone: item.phone || '',
          company: item.company || '',
          message: item.message || 'Demo talebi',
          status: 'new' as const,
          type: 'demo' as const,
          created_at: item.created_at,
          updated_at: item.created_at
        })),
        ...(newsletterRes.data || []).map(item => ({
          id: item.id,
          name: 'Newsletter Abonesi',
          email: item.email,
          phone: '',
          company: '',
          message: 'Newsletter aboneliği',
          status: 'completed' as const,
          type: 'newsletter' as const,
          created_at: item.created_at,
          updated_at: item.created_at
        }))
      ];

      // Tarihe göre sırala (en yeni önce)
      allApplications.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      setApplications(allApplications);
    } catch (error) {
      console.error('Başvurular yüklenirken hata:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...applications];

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(app => app.status === filters.status);
    }

    // Type filter
    if (filters.type !== 'all') {
      filtered = filtered.filter(app => app.type === filters.type);
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(app =>
        app.name.toLowerCase().includes(searchLower) ||
        app.email.toLowerCase().includes(searchLower) ||
        (app.company && app.company.toLowerCase().includes(searchLower))
      );
    }

    // Date filters
    if (filters.dateFrom) {
      filtered = filtered.filter(app => 
        new Date(app.created_at) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(app => 
        new Date(app.created_at) <= new Date(filters.dateTo + 'T23:59:59')
      );
    }

    setFilteredApplications(filtered);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'reviewing': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new': return 'Yeni';
      case 'reviewing': return 'İnceleniyor';
      case 'completed': return 'Tamamlandı';
      case 'archived': return 'Arşiv';
      default: return 'Bilinmiyor';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'contact': return 'İletişim';
      case 'demo': return 'Demo Talebi';
      case 'newsletter': return 'Newsletter';
      default: return 'Bilinmiyor';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'contact': return 'ri-mail-line';
      case 'demo': return 'ri-presentation-line';
      case 'newsletter': return 'ri-newspaper-line';
      default: return 'ri-file-line';
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      type: 'all',
      search: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const openDetailModal = (application: Application) => {
    setSelectedApplication(application);
    setShowDetailModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Başvurular yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tüm Başvurular</h1>
            <p className="text-gray-600 mt-1">Tüm müşterilerden gelen formları buradan takip edin</p>
          </div>
          <div className="flex items-center space-x-3">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {filteredApplications.length} başvuru
            </span>
            <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 whitespace-nowrap">
              <i className="ri-add-line mr-2"></i>
              Yeni Başvuru Ekle
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Arama</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Ad, e-posta ara..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="new">Yeni</option>
              <option value="reviewing">İnceleniyor</option>
              <option value="completed">Tamamlandı</option>
              <option value="archived">Arşiv</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tür</label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Türler</option>
              <option value="contact">İletişim</option>
              <option value="demo">Demo Talebi</option>
              <option value="newsletter">Newsletter</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Clear Filters */}
        {(filters.search || filters.status !== 'all' || filters.type !== 'all' || filters.dateFrom || filters.dateTo) && (
          <div className="mt-4">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i className="ri-close-line mr-2"></i>
              Filtreleri Temizle
            </button>
          </div>
        )}
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-700">ID</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-700">Ad Soyad</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-700">E-posta</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-700">Telefon</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-700">Talep Türü</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-700">Durum</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-700">Tarih</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-700">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map((application, index) => (
                <tr key={application.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6 text-gray-600 font-mono text-sm">
                    #{index + 1}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        application.type === 'contact' ? 'bg-blue-100' :
                        application.type === 'demo' ? 'bg-purple-100' : 'bg-green-100'
                      }`}>
                        <i className={`${getTypeIcon(application.type)} ${
                          application.type === 'contact' ? 'text-blue-600' :
                          application.type === 'demo' ? 'text-purple-600' : 'text-green-600'
                        }`}></i>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{application.name}</div>
                        {application.company && (
                          <div className="text-sm text-gray-600">{application.company}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-gray-600">{application.email}</td>
                  <td className="py-4 px-6 text-gray-600">{application.phone || '-'}</td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      application.type === 'contact' ? 'bg-blue-100 text-blue-800' :
                      application.type === 'demo' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {getTypeText(application.type)}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                      {getStatusText(application.status)}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-gray-600">
                    {new Date(application.created_at).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openDetailModal(application)}
                        className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Görüntüle"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                      <button
                        className="w-8 h-8 flex items-center justify-center text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                        title="Not Ekle"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Sil"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredApplications.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">Henüz başvuru bulunmuyor</p>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedApplication.name} - Başvuru Detayları
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* Müşteri Bilgileri */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Müşteri Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad</label>
                      <p className="text-gray-900">{selectedApplication.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                      <p className="text-gray-900">{selectedApplication.email}</p>
                    </div>
                    {selectedApplication.phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                        <p className="text-gray-900">{selectedApplication.phone}</p>
                      </div>
                    )}
                    {selectedApplication.company && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Şirket</label>
                        <p className="text-gray-900">{selectedApplication.company}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Talep Türü</label>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        selectedApplication.type === 'contact' ? 'bg-blue-100 text-blue-800' :
                        selectedApplication.type === 'demo' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {getTypeText(selectedApplication.type)}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tarih</label>
                      <p className="text-gray-900">
                        {new Date(selectedApplication.created_at).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mesaj */}
                {selectedApplication.message && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Mesaj</h3>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedApplication.message}</p>
                    </div>
                  </div>
                )}

                {/* Durum Değiştirme */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Durum Yönetimi</h3>
                  <div className="flex items-center space-x-4">
                    <select
                      value={selectedApplication.status}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="new">Yeni</option>
                      <option value="reviewing">İnceleniyor</option>
                      <option value="completed">Tamamlandı</option>
                      <option value="archived">Arşiv</option>
                    </select>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
                      Durumu Güncelle
                    </button>
                  </div>
                </div>

                {/* İletişim Butonları */}
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <a
                    href={`mailto:${selectedApplication.email}`}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center whitespace-nowrap"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    E-posta Gönder
                  </a>
                  {selectedApplication.phone && (
                    <a
                      href={`tel:${selectedApplication.phone}`}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-center whitespace-nowrap"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Ara
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApplications;
